/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { createContext, useContext, useState, useEffect } from "react";

interface WorkingHours {
  [day: string]: { start: string; end: string }[];
}

interface WorkingHoursContextType {
  workingHours: WorkingHours | null;
  updateWorkingHours: (hours: WorkingHours) => void;
  manualToggle: boolean;
  setManualToggle: (value: boolean) => void;
  showOfflinePopup: boolean;
  setShowOfflinePopup: (value: boolean) => void;
}

const WorkingHoursContext = createContext<WorkingHoursContextType | undefined>(
  undefined
);

export const WorkingHoursProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(null);
  const [manualToggle, setManualToggle] = useState<boolean>(false);
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);
  // Load from localStorage
  useEffect(() => {
    const savedWH = localStorage.getItem("workingHours");
    if (savedWH) setWorkingHours(JSON.parse(savedWH));

    const savedToggle = localStorage.getItem("manualToggle");
    if (savedToggle) setManualToggle(JSON.parse(savedToggle));
  }, []);
  useEffect(() => {
    localStorage.setItem("manualToggle", JSON.stringify(manualToggle));
  }, [manualToggle]);
  const updateWorkingHours = (hours: WorkingHours) => {
    setWorkingHours(hours);
    localStorage.setItem("workingHours", JSON.stringify(hours));
  };

  return (
    <WorkingHoursContext.Provider
      value={{
        workingHours,
        updateWorkingHours,
        manualToggle,
        setManualToggle,
        showOfflinePopup,
        setShowOfflinePopup,
      }}
    >
      {children}
    </WorkingHoursContext.Provider>
  );
};

export const useWorkingHours = () => {
  const context = useContext(WorkingHoursContext);
  if (!context)
    throw new Error("useWorkingHours must be used inside WorkingHoursProvider");
  return context;
};
